/* eslint-disable react/prop-types */
import React, { useEffect, useState } from "react";
import { Auth } from "aws-amplify";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Box,
  Collapse,
  Typography,
  useMediaQuery,
  makeStyles,
} from "@material-ui/core";
import { useSnackbar } from "notistack";
import { MdKeyboardArrowUp, MdKeyboardArrowDown } from "react-icons/md";
import _ from "lodash";
import { primaryColor } from "resources/constants";
import useDidMountEffect from "lib/react/customHooks";
import getFilteredProductList from "lib/filters/products";
import ScaleLoader from "react-spinners/ScaleLoader";
import { filterScenariosByConstraintsOld } from "lib/filters/scenarios";
import {
  retrieveExpandedScenarioEstimationsByKey,
  fetchScenarioTotalsForFilters,
  cloneScenario,
} from "lib/requests/scenarios";
import getStatus, { PollingTimeoutError } from "lib/requests/status";
import { computeEstimationTotals } from "lib/utils/estimations";
import { getFiltersKey } from "lib/utils";
import { filterEstimations } from "lib/filters/estimations";
import mixpanelTrack from "app/common/MixpanelComponent";
import SnackbarActionButtons from "app/common/snackbar/SnackbarActionButtons";
import TaskProgressSnackbar from "app/common/task/TaskProgressSnackbar";
import { useHistory, useRouteMatch } from "react-router-dom";
import { setTask, removeTask } from "app/common/task/duck/slices";
import { getPathnameByCycletype } from "lib/utils/cycles";
import {
  changeSelectedScenarioAsyncAction,
  addScenarioTotalsAsyncAction,
} from "../duck/actions";
import ScenarioMapChartComponent from "./ScenarioMapChartComponent";
import ScenarioMapFiltersComponent from "./ScenarioMapFiltersComponent";
import ScenarioMapConstraintsComponent from "./constraints/ScenarioMapConstraintsComponent";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "calc(100% - 52px)",
    minHeight: "85vh",
    maxWidth: "1480px",
  },
  dialogContent: {
    padding: "0px",
  },
  dialogActions: {
    borderTop: "1px solid #e2e2e2",
    display: "flex",
  },
  dialogTitle: {
    borderBottom: "1px solid #e2e2e2",
    backgroundColor: primaryColor,
    color: "#fff",
  },
  mapPlayArea: {
    backgroundColor: "#fafafa",
    [theme.breakpoints.up("xs")]: {
      padding: "0px",
      overflow: "auto",
    },
    [theme.breakpoints.up("sm")]: {
      padding: "0px",
      overflow: "auto",
    },
    [theme.breakpoints.up("md")]: {
      padding: "0px",
      overflow: "auto",
    },
    [theme.breakpoints.up("lg")]: {
      padding: "0px 0px 0px 10px",
      overflow: "hidden",
    },
    [theme.breakpoints.up("xl")]: {
      padding: "0px 0px 0px 10px",
      overflow: "hidden",
    },
    flexGrow: "1",
  },
  constraintsArea: {
    padding: "10px",
    height: "100%",
    overflow: "auto",
  },
  dialogContentContainer: {
    /* Removing height 0px will cause constraints overflow:auto to stop working on large screens */
    [theme.breakpoints.up("xs")]: {
      height: "100%",
    },
    [theme.breakpoints.up("sm")]: {
      height: "100%",
    },
    [theme.breakpoints.up("md")]: {
      height: "100%",
    },
    [theme.breakpoints.up("lg")]: {
      height: "0px",
    },
    [theme.breakpoints.up("xl")]: {
      height: "0px",
    },
    display: "flex",
    flexFlow: "column",
    padding: "0px",
    alignItems: "center",
    justifyContent: "center",
  },
  mapChartArea: {
    [theme.breakpoints.up("xs")]: {
      padding: "0px 10px 0px 10px",
    },
    [theme.breakpoints.up("sm")]: {
      padding: "10px",
    },
    position: "relative",
  },
  scenarioActionButtons: {
    "&> :not(:first-child)": {
      marginLeft: theme.spacing(1),
    },
  },
  backDrop: {
    backgroundColor: "rgba(255, 255, 255, 0.7)",
  },
  collapseContainer: {
    minHeight: "auto !important",
  },
  collapseSummary: {
    display: "flex",
    alignItems: "center",
    padding: "10px",
    justifyContent: "space-between",
  },
  smallScreenFiltersContainer: {
    width: "100%",
    backgroundColor: "#fafafa",
  },
  largeScreenFilterContainer: {
    width: "100%",
  },
  smallScreenFiltersContainerInner: {
    margin: "10px",
    borderRadius: "5px",
    border: "1px solid #e2e2e2",
    backgroundColor: "#ffffff",
    boxShadow: "0 1px 4px 0 rgba(37, 50, 56, 0.1)",
  },
  mapChartcontainer: {
    boxShadow: "0 1px 4px 0 rgba(37, 50, 56, 0.1)",
    [theme.breakpoints.up("xs")]: {
      minHeight: "350px",
    },
    [theme.breakpoints.up("lg")]: {
      minHeight: "100%",
    },
    alignItems: "center",
    borderRadius: "3px",
    backgroundColor: "#fff",
    border: "1px solid #e0e0e0",
    position: "relative",
    display: "flex",
    justifyContent: "center",
  },
}));

const splitScenariosByVisibility = (
  scenarioList,
  referenceScenario,
  constraintsList
) => {
  const [
    visibleCustomScenarios,
    disabledCustomScenarios,
  ] = filterScenariosByConstraintsOld(
    _.filter(
      scenarioList,
      (scenarioTotal) => scenarioTotal.scenario.scenario_type_id === 3
    ),
    referenceScenario,
    constraintsList
  );

  const [
    visibleDefaultScenarios,
    disabledDefaultScenarios,
  ] = filterScenariosByConstraintsOld(
    _.filter(
      scenarioList,
      (scenarioTotal) => scenarioTotal.scenario.scenario_type_id === 2
    ),
    referenceScenario,
    constraintsList
  );

  return {
    visibleScenarios: {
      defaultScenarios: visibleDefaultScenarios,
      customScenarios: visibleCustomScenarios,
      referenceScenarios: _.filter(
        scenarioList,
        (scenarioTotal) => scenarioTotal.scenario.scenario_type_id === 1
      ),
    },
    disabledScenarios: disabledCustomScenarios.concat(disabledDefaultScenarios),
  };
};

const computeEstimationTotalsForFilters = (
  estimations,
  referenceEstimations,
  filters,
  productList,
  newInvestment = false
) => {
  const filteredProductList = _.map(
    getFilteredProductList(filters, productList),
    (product) => product.id
  );
  const estimationsForFilters = filterEstimations(
    estimations,
    filteredProductList,
    filters
  );

  const totalsForFilteredEstimations = computeEstimationTotals(
    estimationsForFilters,
    referenceEstimations,
    newInvestment
  );

  return totalsForFilteredEstimations;
};

const computeRangesForScenarioTotals = (scenarioTotals, referenceScenario) => {
  return {
    priceRange: [
      Math.floor(
        100 *
          Math.min(
            ..._.map(scenarioTotals, (scenarioTotal) => {
              return scenarioTotal.min_price_change;
            })
          )
      ),
      Math.ceil(
        100 *
          Math.max(
            ..._.map(scenarioTotals, (scenarioTotal) => {
              return scenarioTotal.max_price_change;
            })
          )
      ),
    ],
    unitsRange: [
      Math.floor(
        100 *
          Math.min(
            ..._.map(scenarioTotals, (scenarioTotal) => {
              return (
                (scenarioTotal.units - referenceScenario.units) /
                referenceScenario.units
              );
            })
          )
      ),
      Math.ceil(
        100 *
          Math.max(
            ..._.map(scenarioTotals, (scenarioTotal) => {
              return (
                (scenarioTotal.units - referenceScenario.units) /
                referenceScenario.units
              );
            })
          )
      ),
    ],
    marginRange: [
      Math.floor(
        100 *
          Math.min(
            ..._.map(scenarioTotals, (scenarioTotal) => {
              return (
                (scenarioTotal.margin - referenceScenario.margin) /
                referenceScenario.margin
              );
            })
          )
      ),
      Math.ceil(
        100 *
          Math.max(
            ..._.map(scenarioTotals, (scenarioTotal) => {
              return (
                (scenarioTotal.margin - referenceScenario.margin) /
                referenceScenario.margin
              );
            })
          )
      ),
    ],
  };
};

const getKeyForCycleAndFilters = (cycle, filters) => {
  return `${cycle.id}-${getFiltersKey(filters)}`;
};

const ScenarioMapComponentOld = ({
  scenarioMapAnchorEl,
  setScenarioMapAnchorEl,
}) => {
  const classes = useStyles();
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("md"));

  const dispatch = useDispatch();

  // Redux store selectors
  const scenariosSlice = useSelector((state) => state.scenarios);
  const metadataSlice = useSelector((state) => state.metadata);
  const catalogSlice = useSelector((state) => state.catalog);
  const areasSlice = useSelector((state) => state.areas);
  const retailersSlice = useSelector((state) => state.retailers);
  const cycleSlice = useSelector((state) => state.cycle);
  const configCatSlice = useSelector((state) => state.auth.configCatUser);

  // Internal component state
  const [cycle, setCycle] = useState(
    scenariosSlice.selectedScenario.scenario.cycle
  );
  const [collapseOpen, setCollapseOpen] = useState(false);

  const [filters] = useState({});

  const [scenarioTotalsForScope, setscenarioTotalsForScope] = useState({});
  const [isScenarioTotalsEmpty, setIsScenarioTotalsEmpty] = useState(false);
  const [
    scenarioMapSelectedScenario,
    setScenarioMapSelectedScenario,
  ] = useState(scenariosSlice.selectedScenario);
  const [
    scenarioMapReferenceScenario,
    setScenarioMapReferenceScenario,
  ] = useState(scenariosSlice.referenceScenario);
  const [newScenarioName, setNewScenarioName] = useState(
    `Copy of ${scenariosSlice.selectedScenario.scenario.name}`
  );
  const [invalidName, setInvalidName] = useState(false);
  const [invalidcharacterInCommment, setInvalidcharacterInCommment] = useState(
    false
  );
  const [invalidcharacter, setInvalidcharacter] = useState(false);
  const [newScenarioComment, setNewScenarioComment] = useState("");
  const [isDataReady, setIsDataReady] = useState(false);
  const [isMapReady, setIsMapReady] = useState(false);

  const [constraints, setConstraints] = useState(
    computeRangesForScenarioTotals(
      scenarioTotalsForScope[getKeyForCycleAndFilters(cycle, filters)],
      scenarioMapReferenceScenario
    )
  );

  const [constraintRanges, setConstraintRanges] = useState(
    computeRangesForScenarioTotals(
      scenarioTotalsForScope[getKeyForCycleAndFilters(cycle, filters)],
      scenarioMapReferenceScenario
    )
  );

  const [scenariosByVisibility, setScenariosByVisibility] = useState(
    splitScenariosByVisibility(
      scenarioTotalsForScope[getKeyForCycleAndFilters(cycle, filters)],
      scenarioMapReferenceScenario,
      constraints
    )
  );

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [arePendingTasks, setArePendingTasks] = React.useState(false);
  const history = useHistory();
  const { url } = useRouteMatch();

  const handleSetScenarioMapClose = () => {
    setScenarioMapAnchorEl(null);
  };

  /**
   * ALL COPY-PASTED CODE FROM WITHCOPYSCENARIO SHOULD BE GENERALIZED IN
   * SOME WAY TO PREVENT CODE DUPLICATION!!
   *
   */

  const preventPageUnload = (e) => {
    e.preventDefault();
    e.returnValue = "";
  };

  React.useEffect(() => {
    if (arePendingTasks)
      window.addEventListener("beforeunload", preventPageUnload);
    else window.removeEventListener("beforeunload", preventPageUnload);
    return () => {
      window.removeEventListener("beforeunload", preventPageUnload);
    };
  }, [arePendingTasks]);

  const enqueueLoadingSnackbar = (action) =>
    enqueueSnackbar(
      `'${scenarioMapSelectedScenario.scenario.name}' (${scenarioMapSelectedScenario.scenario.cycle.label}) is being copied 
              over to '${newScenarioName}' (${scenarioMapSelectedScenario.scenario.cycle.label})...`,
      {
        persist: true,
        action,
      }
    );

  const enqueueErrorSnackbar = (key) => {
    if (key) closeSnackbar(key);
    enqueueSnackbar(
      `Error when copying '${scenarioMapSelectedScenario.scenario.name}' (${scenarioMapSelectedScenario.scenario.cycle.label}) over 
            to '${newScenarioName}' (${scenarioMapSelectedScenario.scenario.cycle.label}). Try again shortly`,
      {
        variant: "error",
      }
    );
    setArePendingTasks(false);
  };

  const enqueueSuccessSnackbar = (onAction, snackbarKey) => {
    if (snackbarKey) closeSnackbar(snackbarKey);
    enqueueSnackbar(
      `Scenario '${newScenarioName}' (${scenarioMapSelectedScenario.scenario.cycle.label}) successfully created!`,
      {
        variant: "success",
        persist: "true",
        action: (key) => (
          <SnackbarActionButtons
            action="Go to Scenario"
            onAction={onAction}
            key={key}
          />
        ),
      }
    );
  };

  const enqueueTimeoutSnackbar = (onAction, snackbarKey) => {
    if (snackbarKey) closeSnackbar(snackbarKey);
    enqueueSnackbar(
      `Could not update the status of the task. Note that if you dismiss this message, 
                    the task can still be completed, but you will not get notified`,
      {
        variant: "warning",
        persist: "true",
        action: (key) => (
          <SnackbarActionButtons
            action="Reconnect"
            onAction={onAction}
            key={key}
            onDismiss={() => setArePendingTasks(false)}
          />
        ),
      }
    );
  };

  const handleApplyScenario = async () => {
    /* MIXPANEL TRACKS */
    mixpanelTrack("Create scenario from map");
    /* END OF MIXPANEL TRACKS */
    const user = await Auth.currentUserInfo();
    handleSetScenarioMapClose();

    let snackbarKey;

    try {
      const { task } = await cloneScenario({
        id: scenarioMapSelectedScenario.scenario.id,
        name: newScenarioName,
        owner_label: user.username,
        owner_id: user.attributes.sub,
      });

      const enqueueTask = async () => {
        setArePendingTasks(true);

        snackbarKey = enqueueLoadingSnackbar(
          <TaskProgressSnackbar
            taskId={task.id}
            size={40}
            className={classes.circularProgress}
          />
        );
        try {
          await getStatus(task.id, (status) => {
            switch (status.task_status) {
              case "FAILURE":
                enqueueErrorSnackbar(snackbarKey);
                break;
              case "SUCCESS": {
                // Success
                // Update list of scenarios and totals
                dispatch(
                  addScenarioTotalsAsyncAction({
                    filters: {
                      scenario_type_id: [1, 3],
                    },
                    expand: ["scenario", "scenario.cycle"],
                  })
                );

                enqueueSuccessSnackbar(() => {
                  const cycleType = _.find(cycleSlice.type, {
                    id: _.find(cycleSlice.list, {
                      id: status.task_info?.result?.cycle_id,
                    }).cycle_type_id,
                  }).label;
                  const scenarioPath = getPathnameByCycletype(cycleType, url);
                  history.push(scenarioPath);
                  dispatch(
                    changeSelectedScenarioAsyncAction(
                      status.task_info?.result?.scenario_id
                    )
                  );
                }, snackbarKey);
                dispatch(removeTask(task.id));
                setArePendingTasks(false);
                return;
              }
              case "PENDING":
                return;
              default:
                dispatch(
                  setTask({
                    id: task.id,
                    progress:
                      (status.task_info?.step * 100) /
                      status.task_info?.total_steps,
                  })
                );
            }
          });
        } catch (e) {
          if (e instanceof PollingTimeoutError) {
            enqueueTimeoutSnackbar(enqueueTask, snackbarKey);
          } else throw e;
        }
      };

      await enqueueTask();
    } catch (e) {
      // Log the error somewhere
      enqueueErrorSnackbar(
        `Error when copying '${scenarioMapSelectedScenario.scenario.name}'. Try again shortly`,
        snackbarKey
      );
    }
  };

  async function updateSelectedScenario(scenario) {
    setScenarioMapSelectedScenario({
      ...scenario,
      scenario: {
        ...scenario.scenario,
        cycle,
      },
    });
  }

  async function filterScenariosForScope() {
    let newScenarioMapSelectedScenario = {};
    let filteredScenarioTotals;

    setIsMapReady(false);

    if (!scenarioTotalsForScope[getKeyForCycleAndFilters(cycle, filters)]) {
      filteredScenarioTotals = await fetchScenarioTotalsForFilters(
        { cycle_id: cycle.id } || {}
      );

      filteredScenarioTotals = filteredScenarioTotals.data;
    } else {
      filteredScenarioTotals =
        scenarioTotalsForScope[getKeyForCycleAndFilters(cycle, filters)];
    }

    if (_.isEmpty(filteredScenarioTotals)) {
      setIsMapReady(true);
      setscenarioTotalsForScope({
        ...scenarioTotalsForScope,
        [getKeyForCycleAndFilters(cycle, filters)]: [],
      });
      setIsScenarioTotalsEmpty(true);
      return;
    }
    setIsScenarioTotalsEmpty(false);

    // First of all we want to update the reference scenario
    let newReferenceScenario = _.find(
      filteredScenarioTotals,
      (scenarioTotal) => scenarioTotal.scenario.scenario_type_id === 1
    );

    newReferenceScenario = {
      ...newReferenceScenario,
      scenario: { ...newReferenceScenario.scenario, cycle },
      estimations: await retrieveExpandedScenarioEstimationsByKey(
        {},
        catalogSlice,
        retailersSlice,
        areasSlice,
        newReferenceScenario.scenario_id
      ),
    };

    setScenarioMapReferenceScenario(newReferenceScenario);

    const filteredScenarioTotalsWithSelectedScenario = _.map(
      filteredScenarioTotals,
      (scenarioTotal) => {
        // We do this to mark the selected scenario in the map after changing the scope since otherwise every time a filter changed the selected scenario would not be highlighted in the map
        if (
          !_.isEmpty(scenarioMapSelectedScenario) &&
          scenarioTotal.scenario.id === scenarioMapSelectedScenario.scenario.id
        ) {
          if (
            scenarioTotal.scenario.id ===
            scenariosSlice.selectedScenario.scenario.id
          ) {
            newScenarioMapSelectedScenario = {
              ...scenarioTotal,
              ...computeEstimationTotalsForFilters(
                scenariosSlice.selectedScenario.estimations,
                newReferenceScenario.estimations,
                filters,
                catalogSlice.products,
                configCatSlice?.features.newInvestmentCalculation
              ),
              scenario: { ...scenarioTotal.scenario, cycle },
              estimations: scenarioMapSelectedScenario.estimations,
            };
          } else {
            newScenarioMapSelectedScenario = {
              ...scenarioTotal,
              scenario: { ...scenarioTotal.scenario, cycle },
              estimations: scenarioMapSelectedScenario.estimations,
            };
          }
        }

        // We do this because the selected scenario may not yet be updated in the backend at the time of the request since this update is done asynchronously so we use the data updated in the front
        return scenarioTotal.scenario.id ===
          scenariosSlice.selectedScenario.scenario.id
          ? {
              ...scenarioTotal,
              ...computeEstimationTotalsForFilters(
                scenariosSlice.selectedScenario.estimations,
                newReferenceScenario.estimations,
                filters,
                catalogSlice.products,
                configCatSlice?.features.newInvestmentCalculation
              ),
              scenario: { ...scenarioTotal.scenario, cycle },
            }
          : {
              ...scenarioTotal,
              scenario: { ...scenarioTotal.scenario, cycle },
            };
      }
    );

    setScenarioMapSelectedScenario(newScenarioMapSelectedScenario);
    setscenarioTotalsForScope({
      ...scenarioTotalsForScope,
      [getKeyForCycleAndFilters(
        cycle,
        filters
      )]: filteredScenarioTotalsWithSelectedScenario,
    });
    setScenariosByVisibility(
      splitScenariosByVisibility(
        filteredScenarioTotalsWithSelectedScenario,
        scenarioMapReferenceScenario,
        constraints
      )
    );

    setIsDataReady(true);
    setIsMapReady(true);
  }

  // Effects
  useDidMountEffect(() => {
    filterScenariosForScope();
  }, [cycle]);

  useDidMountEffect(() => {
    filterScenariosForScope();
  }, [filters]);

  useEffect(() => {
    setScenariosByVisibility(
      splitScenariosByVisibility(
        scenarioTotalsForScope[getKeyForCycleAndFilters(cycle, filters)],
        scenarioMapReferenceScenario,
        constraints
      )
    );
  }, [constraints, scenarioMapSelectedScenario.id]);

  useEffect(() => {
    if (
      _.find(
        scenarioTotalsForScope[getKeyForCycleAndFilters(cycle, filters)],
        (scenario) => scenario.scenario.name === newScenarioName
      )
    ) {
      setInvalidName(true);
    } else setInvalidName(false);

    setInvalidcharacter(
      !/^[A-Za-z0-9\sÁÉÍÓÚáéíóúÜüÑñ-]+$/.test(newScenarioName.trim())
    );
  }, [newScenarioName]);

  useEffect(() => {
    if (newScenarioComment)
      setInvalidcharacterInCommment(
        newScenarioComment.trim().includes("|") ||
          newScenarioComment.trim().includes(",")
      );
  }, [newScenarioComment]);

  useEffect(() => {
    /* MIXPANEL TRACKS */
    mixpanelTrack("Scenario map visited");
    /* END OF MIXPANEL TRACKS */

    filterScenariosForScope();
  }, []);

  useEffect(() => {
    setConstraintRanges(
      computeRangesForScenarioTotals(
        scenarioTotalsForScope[getKeyForCycleAndFilters(cycle, filters)],
        scenarioMapReferenceScenario
      )
    );
    setConstraints(
      computeRangesForScenarioTotals(
        scenarioTotalsForScope[getKeyForCycleAndFilters(cycle, filters)],
        scenarioMapReferenceScenario
      )
    );
  }, [scenarioTotalsForScope[getKeyForCycleAndFilters(cycle, filters)]]);

  const getMapPlayAreaContent = () => {
    if (isMapReady) {
      if (isScenarioTotalsEmpty) {
        return <div>There are no scenarios for the current filters.</div>;
      }
      return (
        <ScenarioMapChartComponent
          metadata={metadataSlice}
          scenariosList={scenariosByVisibility.visibleScenarios}
          disabledScenariosList={scenariosByVisibility.disabledScenarios}
          scenarioMapSelectedScenario={scenarioMapSelectedScenario}
          scenarioMapReferenceScenario={scenarioMapReferenceScenario}
          updateSelectedScenario={updateSelectedScenario}
        />
      );
    }
    return <ScaleLoader size={150} color={primaryColor} loading />;
  };

  return (
    <Dialog
      aria-labelledby="transition-Dialog-title"
      aria-describedby="transition-Dialog-description"
      open={Boolean(scenarioMapAnchorEl)}
      maxWidth="lg"
      onClose={handleSetScenarioMapClose}
      classes={{ paper: classes.root }}
      BackdropProps={{
        classes: {
          root: classes.backDrop,
        },
      }}
    >
      <DialogTitle className={classes.dialogTitle} id="scenario-map-title">
        Scenario Map
      </DialogTitle>
      <DialogContent className={classes.dialogContentContainer}>
        {isDataReady ? (
          <>
            {isSmallScreen ? (
              <div className={classes.smallScreenFiltersContainer}>
                <div className={classes.smallScreenFiltersContainerInner}>
                  <Box
                    onClick={() => setCollapseOpen(!collapseOpen)}
                    className={`${classes.collapseSummary}`}
                  >
                    <Typography>Filters</Typography>
                    {collapseOpen ? (
                      <MdKeyboardArrowUp size={22} />
                    ) : (
                      <MdKeyboardArrowDown size={22} />
                    )}
                  </Box>
                  <Collapse
                    in={collapseOpen}
                    timeout={0}
                    unmountOnExit
                    classes={{ container: classes.collapseContainer }}
                  >
                    <ScenarioMapFiltersComponent
                      cycle={cycle}
                      setCycle={setCycle}
                    />
                  </Collapse>
                </div>
              </div>
            ) : (
              <div className={classes.largeScreenFilterContainer}>
                <ScenarioMapFiltersComponent
                  cycle={cycle}
                  setCycle={setCycle}
                />
              </div>
            )}

            <Grid container item className={classes.mapPlayArea}>
              <Grid
                item
                xl={8}
                lg={8}
                md={12}
                sm={12}
                xs={12}
                className={classes.mapChartArea}
              >
                <div className={classes.mapChartcontainer}>
                  {getMapPlayAreaContent()}
                </div>
              </Grid>
              <Grid
                container
                item
                xl={4}
                lg={4}
                md={12}
                sm={12}
                xs={12}
                className={classes.constraintsArea}
              >
                <ScenarioMapConstraintsComponent
                  newScenarioName={newScenarioName}
                  constraintRanges={constraintRanges}
                  setNewScenarioName={setNewScenarioName}
                  invalidName={invalidName}
                  invalidcharacter={invalidcharacter}
                  invalidcharacterInCommment={invalidcharacterInCommment}
                  newScenarioComment={newScenarioComment}
                  setNewScenarioComment={setNewScenarioComment}
                  constraints={constraints}
                  filters={filters}
                  setConstraints={setConstraints}
                  scenarioMapSelectedScenario={scenarioMapSelectedScenario}
                  scenarioMapReferenceScenario={scenarioMapReferenceScenario}
                  metadata={metadataSlice}
                />
              </Grid>
            </Grid>
          </>
        ) : (
          <ScaleLoader size={150} color={primaryColor} loading />
        )}
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <Box component="div" className={classes.scenarioActionButtons}>
          <Button onClick={handleSetScenarioMapClose}>Close</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleApplyScenario}
            disabled={
              _.isEmpty(scenarioMapSelectedScenario) ||
              invalidName ||
              invalidcharacter ||
              invalidcharacterInCommment ||
              configCatSlice?.features?.IsMaintenanceActive
            }
          >
            Create Scenario
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ScenarioMapComponentOld;
